import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Check, ChevronsUpDown, Languages, LogOut, UserRound } from "lucide-react";
import { useNavigate } from "react-router";

import { useCurrentUser, useSession } from "@/app/session/useSession";
import { firstNameOf } from "@/features/home/lib/greeting";
import { useLocale, useT } from "@/shared/i18n/useT";
import { cn } from "@/shared/lib/cn";
import { THEME_OPTIONS } from "@/shared/theme/options";
import { isThemePreference } from "@/shared/theme/theme";
import { useTheme } from "@/shared/theme/useTheme";
import { Avatar } from "@/shared/ui/Avatar";
import { menuContentClassName, menuItemClassName, menuLabelClassName } from "@/shared/ui/styles";

const ITEM = cn(menuItemClassName, "[&_svg]:text-ink-muted");
const SEPARATOR = "-mx-1 my-1 h-px bg-line";

/** Light, dark or the system's: three radio items, the current one checked. */
function ThemeChoices() {
  const t = useT();
  const { preference, setPreference } = useTheme();
  return (
    <DropdownMenu.Group>
      <DropdownMenu.Label className={menuLabelClassName}>{t("theme.title")}</DropdownMenu.Label>
      <DropdownMenu.RadioGroup
        value={preference}
        onValueChange={(value) => {
          if (isThemePreference(value)) setPreference(value);
        }}
      >
        {THEME_OPTIONS.map(({ value, labelId, icon: Icon }) => (
          <DropdownMenu.RadioItem
            key={value}
            value={value}
            // Keep the menu open, so the change shows behind it and can be undone at once.
            onSelect={(event) => {
              event.preventDefault();
            }}
            className={ITEM}
          >
            <Icon aria-hidden="true" />
            <span className="flex-1">{t(labelId)}</span>
            <DropdownMenu.ItemIndicator>
              <Check className="text-brand-strong!" aria-hidden="true" />
            </DropdownMenu.ItemIndicator>
          </DropdownMenu.RadioItem>
        ))}
      </DropdownMenu.RadioGroup>
    </DropdownMenu.Group>
  );
}

/**
 * The signed-in person at the bottom of the sidebar: name and role, and a menu with the profile,
 * the colour theme, the language and signing out.
 */
export function UserMenu({
  onNavigate,
  compact = false,
}: {
  onNavigate?: () => void;
  /** Avatar only, for the folded sidebar. */
  compact?: boolean;
}) {
  const t = useT();
  const user = useCurrentUser();
  const { signOut } = useSession();
  const { toggleLocale } = useLocale();
  const navigate = useNavigate();
  const role = user.role ? t(`role.${user.role}`) : null;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        className={cn(
          "flex items-center gap-3 rounded-xl border border-line bg-surface/60 text-start",
          compact ? "p-1" : "w-full p-2.5",
          "transition-[background-color,border-color,box-shadow] outline-none hover:border-line hover:bg-surface hover:shadow-xs focus-visible:border-line focus-visible:bg-surface data-[state=open]:border-line data-[state=open]:bg-surface data-[state=open]:shadow-xs",
        )}
        aria-label={t("topbar.accountMenu")}
      >
        <Avatar name={user.name} src={user.avatar_url} className="size-10 text-[14px]" />
        <span className={cn("min-w-0 flex-1 leading-tight", compact && "sr-only")}>
          <span
            title={user.name}
            className="user-text block truncate text-[15px] font-semibold text-ink"
          >
            {firstNameOf(user.name)}
          </span>
          {role ? (
            <span className="mt-0.5 block truncate text-[13px] text-ink-subtle">{role}</span>
          ) : null}
        </span>
        {compact ? null : (
          <ChevronsUpDown className="size-4 shrink-0 text-ink-faint" aria-hidden="true" />
        )}
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          side="top"
          align="start"
          sideOffset={6}
          className={cn(menuContentClassName, "w-(--radix-dropdown-menu-trigger-width) min-w-64")}
        >
          <div className="flex items-center gap-2.5 px-2 py-2">
            <Avatar name={user.name} src={user.avatar_url} className="size-8 text-[12px]" />
            <div className="min-w-0 leading-tight">
              <p className="user-text truncate text-[14px] font-medium text-ink">{user.name}</p>
              <p dir="ltr" className="mt-0.5 truncate text-start text-[12.5px] text-ink-subtle">
                {user.email}
              </p>
            </div>
          </div>
          <DropdownMenu.Separator className={SEPARATOR} />
          <DropdownMenu.Item
            onSelect={() => {
              onNavigate?.();
              void navigate("/settings/profile");
            }}
            className={ITEM}
          >
            <UserRound aria-hidden="true" />
            {t("profile.title")}
          </DropdownMenu.Item>
          <DropdownMenu.Item onSelect={toggleLocale} className={ITEM}>
            <Languages aria-hidden="true" />
            <span className="flex-1">{t("palette.switchLanguage")}</span>
          </DropdownMenu.Item>
          <DropdownMenu.Separator className={SEPARATOR} />
          <ThemeChoices />
          <DropdownMenu.Separator className={SEPARATOR} />
          <DropdownMenu.Item onSelect={() => void signOut()} className={ITEM}>
            <LogOut className="rtl:-scale-x-100" aria-hidden="true" />
            {t("auth.signOut")}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
